import './crfListing.css';

import { useCallback, useEffect, useState } from "react";

import { getCrfListing } from '../../api/searchApi';
import { useParams } from 'react-router-dom';
import SearchForm from './searchForm';
import { getInput } from '../../api/inputApi';
import CrfGrouping from './crfGrouping';
import Spinner from '../Layouts/Spinner/spinner';
import ViewHeader from '../Layouts/ViewHeader/ViewHeader';
import { getDocUrl } from '../Layouts/CrfDocLink/docUrl';

const defaultFormData = {
    keyword: '',
    disease: '',
    subDisease: '',
    domain: '',
    subDomain: '',
    copyright: ''
};

const defaultFilters = {
    subDisease: [],
    domain: [],
    subDomain: [],
    copyright: []
};

const CrfListing = () => {
    const [results, setResults] = useState(null);
    const [formData, setFormData] = useState(defaultFormData);
    const [filters, setFilters] = useState(defaultFilters);
    const [isSearching, setIsSearching] = useState(false);

    const { diseaseId } = useParams();

    useEffect(() => {
        const doFetch = async () => {
            const promises = [
                getInput('subDisease', diseaseId),
                getInput('domain', diseaseId),
                getInput('subDomain', diseaseId),
                Promise.resolve([{ id: 'True', name: 'True' }, { id: 'False', name: 'False' }])
            ];

            const [subDiseases, domains, subDomains, copyrights] = await Promise.all(promises);

            setFilters({
                subDisease: subDiseases,
                domain: domains,
                subDomain: subDomains,
                copyright: copyrights
            });
        };

        doFetch();
    }, []);

    const doSearch = useCallback((initData) => {
        const search = async (initData) => {
            setIsSearching(true);
            setResults(null);

            const results = await getCrfListing(initData || formData);
            // console.log(results);

            setResults(results);
            setIsSearching(false);
        }

        search(initData);
    }, [formData]);

    // initial search with just disease from the URL param
    useEffect(() => {
        doSearch({ ...defaultFormData, disease: diseaseId });
    }, []);

    const buttonSearchHandler = useCallback((e) => {
        e.preventDefault();

        doSearch();
    }, [doSearch]);

    const formUpdateHandler = useCallback((e, fieldName) => {
        setFormData(prevFormData => {
            const newFormData = { ...prevFormData };

            newFormData[fieldName] = e.target.value;
            newFormData.disease = diseaseId;

            return newFormData;
        })
    }, [diseaseId]);

    const buttonClearHandler = useCallback(() => {
        setFormData({ ...defaultFormData });
        doSearch({ ...defaultFormData, disease: diseaseId });
    }, []);

    return (
        <>
            {/* <div className="search-panel">
                <ViewHeader>
                    <h3><strong>In the Form Search below, select a Subdisease for your TBI study to view the CDEs for those types of studies.</strong></h3>
                    <p>The default setting for Subdisease is ‘Any’ and will return results for all Subdiseases.</p>
                    <p>Choose your type of TBI study (Acute Hospitalized, Concussion/Mild TBI, Moderate/Severe TBI: Rehabilitation, or Epidemiology) or Comprehensive if your study falls outside of the study types or incorporates aspects of more than one type of study.</p>
                    <p>The Comprehensive template CRFs include the Core CDEs and CDEs Basic to at least one of the study types. Additional Supplemental CDEs are listed for possible inclusion at the bottom of the template CRFs. Recommended proprietary instruments/scales are included on this page regardless of classification. For the classifications assigned to instruments/scales, please see <a href="https://www.commondataelements.ninds.nih.gov/sites/nindscde/files/Doc/TBI/Table_of_Recommended_Outcome_Measures2.xls">Table of Recommended Outcome Measures</a>.</p>
                </ViewHeader>
            </div> */}
            <div className="search-panel">
                <ViewHeader>
                    <h3><strong>Form Search Instructions - Please Read Before Searching</strong></h3>
                    <p>The Form Search is designed to filter the CRF Modules, Guidance Documents, and NOCs on this page by Search Term, Domain, Subdomain, and/or Copyright Status. The Copyright field is designed to filter between forms that are freely available online and forms that are copyrighted or trademarked. Please note that regardless of copyright/trademark status, these instruments are not available on this website. All recommended instruments, regardless of copyright status, have informational documents posted on the NINDS CDE website which provide details concerning availability.</p>
                    <p><strong>Helpful Hints:</strong></p>
                    <p>Once you have completed your search, the search results will need to be expanded by clicking the Expand All button to view results.</p>
                    <p>The default setting for Domain, Subdomain and Copyright fields is ‘Any’.</p>
                    <p>Leave the Search Term(s) field blank to return all results or enter search terms or keywords to return any forms that match the terms that are entered.</p>
                    <p>For best results, click the Clear button between searches.</p>

                    <p>Please contact NINDS CDE team (<a href="mailto:NINDSCDE@emmes.com">NINDSCDE@emmes.com</a>) in case you encounter any search difficulties.</p>
                    <p><a href={getDocUrl('Doc/NINDS_CDE_Form%20Search.pdf')} target="_blank"><strong>Click here for additional instructions</strong></a></p>
                </ViewHeader>
                <SearchForm
                    formData={formData}
                    formUpdateHandler={formUpdateHandler}
                    filters={filters}
                    buttonSearchHandler={buttonSearchHandler}
                    buttonClearHandler={buttonClearHandler}
                    subDiseaseLabel={diseaseId === 'SRC' ? 'Time Frame' : 'Subdisease Name'}
                />
            </div>
            {isSearching && <Spinner />}
            {results && <CrfGrouping results={results} diseaseId={diseaseId} />}
            <ViewHeader>
                <h3>Prospective Assessments for Suicidal Ideation and Behavior</h3>
                <p>Investigators should review the FDA’s <a href="http://www.fda.gov/Drugs/GuidanceComplianceRegulatoryInformation/Guidances/ucm315156.htm" target="_blank">"Guidance for Industry: Suicidal Ideation and Behavior: Prospective Assessment of Occurrence in Clinical Trials”</a> for the most up-to-date information about suicidal ideation and behavior. One scale that FDA suggests is the Columbia Suicide Severity Rating Scale (C-SSRS) (available at <a href="https://cssrs.columbia.edu/" target="_blank" title="external web site">https://cssrs.columbia.edu/</a>).</p>
                {/* <h4>Instrument Notice of Copyright Information</h4>
                <p><em>The NINDS CDE Team does not post proprietary instruments/scales recommended by the CDE Working Groups on this website. This includes, but is not limited to, copyrighted or trademarked instruments/scales. Information about recommended instruments can be found in the Notice of Copyright (NOC) documents under ‘CRF Module/Guidance’ on each disorder’s data standards page. For any questions regarding these instruments/scales please contact the corresponding owner/author. The NINDS CDE Team is not responsible for the availability or content of these external sites, nor does the NINDS CDE Team endorse, warrant or guarantee the products, services or information described or offered at these other internet sites.</em></p> */}
            </ViewHeader>
        </>
    );
};

export default CrfListing;